import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { ComponentBase, Changeable } from "common";
import { LayoutSettings, LayoutType, RootAlignment, LayoutDirection } from 'DiscoveryEngine.TS/Topology';


@Component({
    selector: 'layout-settings',
	templateUrl: './layout-settings.component.html',
	styleUrls: ['./layout-settings.component.scss'],
//	changeDetection: ChangeDetectionStrategy.OnPush
})
export class LayoutSettingsComponent extends ComponentBase implements Changeable {

	@Input()
	public target: LayoutSettings;

	@Input()
	public allowManualLayout: boolean;

	public get enableApply(): boolean {
		return (!this.form.pristine && this.form.valid);
	}

	public form: UntypedFormGroup;

	public layoutType: UntypedFormControl;
	public radialMinRadius: UntypedFormControl;
	public radialMaxRadius: UntypedFormControl;
	public radialMaxAngle: UntypedFormControl;
	public treeLevelSpacing: UntypedFormControl;
	public treeNodeSpacing: UntypedFormControl;
	public treeRootAlignment: UntypedFormControl;
	public treeLayoutDirection: UntypedFormControl;
	public treeUseStraightLinks: UntypedFormControl;
	public ringMinRadius: UntypedFormControl;
	public ringMaxRadius: UntypedFormControl;
	public ringMaxAngle: UntypedFormControl;

	constructor(private fb: UntypedFormBuilder) {
		super();

		this.target = null;

		this.buildForm();
	}

	protected buildForm(): void {

		let layoutType = "Radial";
		let radialMinRadius = 0;
		let radialMaxRadius = 0;
		let radialMaxAngle = 0;
		let treeLevelSpacing = 0;
		let treeNodeSpacing = 0;
		let treeRootAlignment = "Center";
		let treeLayoutDirection = "Down";
		let treeUseStraightLinks = false;
		let ringMinRadius = 0;
		let ringMaxRadius = 0;
		let ringMaxAngle = 0;

		if (this.target) {
			let layoutSettings = this.target;

			layoutType = LayoutType[layoutSettings.LayoutType];

			radialMinRadius = layoutSettings.RadialSettings.MinRadius;
			radialMaxRadius = layoutSettings.RadialSettings.MaxRadius;
			radialMaxAngle = layoutSettings.RadialSettings.MaxAngle;

			treeLevelSpacing = layoutSettings.HierarchicalSettings.LevelSpacing;
			treeNodeSpacing = layoutSettings.HierarchicalSettings.NodeSpacing;
			treeRootAlignment = RootAlignment[layoutSettings.HierarchicalSettings.RootAlignment];
			treeLayoutDirection = LayoutDirection[layoutSettings.HierarchicalSettings.LayoutDirection];
			treeUseStraightLinks = layoutSettings.HierarchicalSettings.UseStraightLinks;

			ringMinRadius = layoutSettings.RingSettings.MinRadius;
			ringMaxRadius = layoutSettings.RingSettings.MaxRadius;
			ringMaxAngle = layoutSettings.RingSettings.MaxAngle;
		}

		this.layoutType = new UntypedFormControl(layoutType);
		this.radialMinRadius = new UntypedFormControl(radialMinRadius, Validators.compose([Validators.required, this.minValue(0), this.maxValue(9999)]));
		this.radialMaxRadius = new UntypedFormControl(radialMaxRadius, Validators.compose([Validators.required, this.minValue(0), this.maxValue(9999)]));
		this.radialMaxAngle = new UntypedFormControl(radialMaxAngle, Validators.compose([Validators.required, this.minValue(0), this.maxValue(999)]));
		this.treeLevelSpacing = new UntypedFormControl(treeLevelSpacing, Validators.compose([Validators.required, this.minValue(0), this.maxValue(9999)]));
		this.treeNodeSpacing = new UntypedFormControl(treeNodeSpacing, Validators.compose([Validators.required, this.minValue(0), this.maxValue(9999)]));
		this.treeRootAlignment = new UntypedFormControl(treeRootAlignment);
		this.treeLayoutDirection = new UntypedFormControl(treeLayoutDirection);
		this.treeUseStraightLinks = new UntypedFormControl(treeUseStraightLinks);
		this.ringMinRadius = new UntypedFormControl(ringMinRadius, Validators.compose([Validators.required, this.minValue(0), this.maxValue(9999)]));
		this.ringMaxRadius = new UntypedFormControl(ringMaxRadius, Validators.compose([Validators.required, this.minValue(0), this.maxValue(9999)]));
		this.ringMaxAngle = new UntypedFormControl(ringMaxAngle, Validators.compose([Validators.required, this.minValue(0), this.maxValue(999)]));

		this.form = this.fb.group({
			layoutType: this.layoutType,
			radialMinRadius: this.radialMinRadius,
			radialMaxRadius: this.radialMaxRadius,
			radialMaxAngle: this.radialMaxAngle,
			treeLevelSpacing: this.treeLevelSpacing,
			treeNodeSpacing: this.treeNodeSpacing,
			treeRootAlignment: this.treeRootAlignment,
			treeLayoutDirection: this.treeLayoutDirection,
			treeUseStraightLinks: this.treeUseStraightLinks,
			ringMinRadius: this.ringMinRadius,
			ringMaxRadius: this.ringMaxRadius,
			ringMaxAngle: this.ringMaxAngle
		});
	}

	public ngOnChanges(changes: SimpleChanges): void {

		if (changes["target"]) {
		}

		this.buildForm();
	}


	public get tempChanges(): boolean {
		return this.form.dirty;
	}

	public get tempChangesValid(): boolean {
		return this.form.valid;
	}

	public discardTempChanges(): void {
		this.buildForm();
	}

	public get invalidTempChanges(): string[] {
		return ["Layout Settings"];
	}

	public applyTempChanges(): void {
		if (this.target) {
			this.getChanges();
			this.buildForm();
		}
	}

	private getChanges(): void {
		let layoutSettings = this.target;

		layoutSettings.LayoutType = LayoutType[<string>this.layoutType.value];
		layoutSettings.RadialSettings.MinRadius = parseInt(this.radialMinRadius.value);
		layoutSettings.RadialSettings.MaxRadius = parseInt(this.radialMaxRadius.value);
		layoutSettings.RadialSettings.MaxAngle = parseInt(this.radialMaxAngle.value);
		layoutSettings.HierarchicalSettings.LevelSpacing = parseInt(this.treeLevelSpacing.value);
		layoutSettings.HierarchicalSettings.NodeSpacing = parseInt(this.treeNodeSpacing.value);
		layoutSettings.HierarchicalSettings.RootAlignment = RootAlignment[<string>this.treeRootAlignment.value];
		layoutSettings.HierarchicalSettings.LayoutDirection = LayoutDirection[<string>this.treeLayoutDirection.value];
		layoutSettings.HierarchicalSettings.UseStraightLinks = this.treeUseStraightLinks.value;
		layoutSettings.RingSettings.MinRadius = parseInt(this.ringMinRadius.value);
		layoutSettings.RingSettings.MaxRadius = parseInt(this.ringMaxRadius.value);
		layoutSettings.RingSettings.MaxAngle = parseInt(this.ringMaxAngle.value);
	}

}
